//================================================================
//  Component: Assign Task
//================================================================

//  Purpose: This is an input field that allows you to search a list of LL users and assign them to a digital request/task

//  Properties:
//    - query = {An array, this contains the query that filters the group of users you would like to search}
//    - currentRequest = {A useState, this contains the current digital request object}
//    - styleInput = <OPTIONAL> {object, CSS style object}
//    - disabled = <OPTIONAL> {boolean, determines if the field is disabled}
//    - lastItem = <OPTIONAL> {boolean, to flip the select box upside down}

//  Example:
//  <AssignTask
//      query={['roles.businessPartnerUser', '==', true]}
//      currentRequest={request}
//      styleInput={{ width: '400px' }}
//      lastItem={((index + 1) === requests?.inProgressRequestsFiltered.length)}
//  ></AssignTask>

//================================================================


//Libraries
import React, { useContext, useReducer, useEffect, useRef } from 'react';

//Contexts
import { GetUser } from '../../Library/GlobalContexts';
import { SetToast } from '../../Library/GlobalContexts';

//Components

//Functions
import WriteDocument from '../../Library/WriteDocument';
import QueryListener from '../../Library/QueryListener';
import AddRemoveDocumentArray from '../../Library/AddRemoveDocumentArray';

//Images
import EditIcon from '../Images/Icon_Edit_Teal.svg';

export default function AssignTask({
    query,
    currentRequest,
    styleInput,
    disabled,
    lastItem,
}) {

    //------------------------------------------------------
    //  useContext
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const setToast = useContext(SetToast);

    //------------------------------------------------------
    //  useRef
    //------------------------------------------------------

    // Reference to the dropdown 
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store the search data
    const [searchData, setSearchData] = useReducer(
        (state, newState) => ({...state, ...newState}),
        {
            'defaultUsers': [],
            'searchResults': [],
            'newUserInput': '',
            'newUserName': '',
            'queryMessage': '',
            'hideDropdown': true
        }
    );

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Function used to handle search
    function handleSearch(value) {
        
        setSearchData({
            'searchResults': [],
            'newUserInput': value,
            'hideDropdown': false
        })

        // Validate input
        if (value.length === 0) {

            return setSearchData({ 
                'searchResults': searchData?.defaultUsers,
                'newUserInput': value,
                'hideDropdown': false, // Show Dropdown
                'queryMessage': ''
            });

        }

        // Filter by characters in email
        const filteredResults = searchData?.defaultUsers?.filter((user) => user?.searcharray?.includes(value.toLowerCase()))

        setSearchData({
            'searchResults': filteredResults,
            'queryMessage': filteredResults?.length > 0 ? `Showing ${filteredResults?.length} result(s)` : ''
        });

    }

    // Used to change the assigned to field
    function handleSave(user) {

        //=================================================
        //  Update useReducer
        //=================================================
  
        setSearchData({ 
            'searchResults': [],
            'newUserInput': user?.emailaddress,
            'newUserName': `${user?.givenname} ${user?.surname}`,
            'hideDropdown': true
        });
  
        //=================================================
        //  Update document in Firestore
        //=================================================

        const docPromises = [];

        const updatedDoc = {
            'assignedto': {
                'email': '',
                'givenname': '',
                'surname': ''
            },
            'assigneddate': new Date(),
            'lastmodifiedby': {
                'email': getUser?.emailaddress,
                'givenname': getUser?.givenname,
                'surname': getUser?.surname
            },
            'lastmodifieddate': new Date(),
        };

        // Handle Assignment to the Same User
        if (user?.emailaddress === currentRequest?.assignedto?.email) return;

        // Handle Unassigned 
        if (user?.emailaddress === 'Unassigned') {

            // Change Assigned To
            updatedDoc.assignedto = {
                'email': 'Unassigned',
                'givenname': '',
                'surname': ''
            }

            // Update Status
            updatedDoc.status = 'Not Started';
            
            // Update the activity feed
            if (currentRequest?.status !== 'Not Started') {

                const updatedActivityFeed = {
                    'activitydate': new Date(),
                    'actionedby': {
                        'email': currentRequest?.assignedto?.email,
                        'givenname': currentRequest?.assignedto?.givenname,
                        'surname': currentRequest?.assignedto?.surname
                    },
                    'action': 'unassigned',
                    'comments': '',
                }

                const modifiedBy = {
                    'email': getUser.emailaddress,
                    'givenname': getUser.givenname,
                    'surname': getUser.surname,
                };

                docPromises.push(
                    AddRemoveDocumentArray('requests', currentRequest?.requestid, 'activityfeed', updatedActivityFeed, 'add', modifiedBy)
                );

            }

        // New User Assigned
        } else {
         
            // Update activity feed 
            const updatedActivityFeed = {
                'activitydate': new Date(),
                'actionedby': {
                    'email': user?.emailaddress,
                    'givenname': user?.givenname,
                    'surname': user?.surname
                },
                'action': 'assigned',
                'comments': '',            
            };

            const modifiedBy = {
                'email': getUser.emailaddress,
                'givenname': getUser.givenname,
                'surname': getUser.surname,
            };

            docPromises.push(
                AddRemoveDocumentArray('requests', currentRequest?.requestid, 'activityfeed', updatedActivityFeed, 'add', modifiedBy)
            );

            // Change Assigned To
            updatedDoc.assignedto = {
                'email': user?.emailaddress,
                'givenname': user?.givenname,
                'surname': user?.surname
            }
            
            // Update Status
            updatedDoc.status = 'In Progress';

        }

        docPromises.push(
            WriteDocument('requests', currentRequest?.requestid, updatedDoc, true)
        );

        return Promise.all(docPromises)        
        .catch((error) =>{

            console.log('Error', error);
                      
            // Set error on toast
            setToast({
                'type': 'error',
                'message': 'Failed to assign task',
            });
  
        })
  
    }

    // Function to close dropdown
    function closeDropdown() {
        setSearchData({ 'hideDropdown': true });
    }

    //------------------------------------------------------
    //  useEffect
    //------------------------------------------------------
    
    // Query Listener
    // - Get users
    useEffect(() => {
  
        if (query === null) return;
  
        function onLoadChange(documents){

            // Filter out users where status is 'inactive'
            const activeDocuments = documents.filter((doc) => doc.status !== 'inactive');

            // Split email into searchable array
            const emailaddress = 'Unassigned';
            let previousValue;
            const emailArray = [];

            [...emailaddress].forEach((currentValue) => {

                // Create an array of each character combination
                emailArray.push(`${previousValue}${currentValue}`.toLowerCase());
                previousValue = `${previousValue}${currentValue}`.toLowerCase();
    
            });

            setSearchData({
                'newUserInput': currentRequest?.assignedto?.email,
                'newUserName': `${currentRequest?.assignedto?.givenname} ${currentRequest?.assignedto?.surname}`,
                'defaultUsers': [
                    {
                        'emailaddress': 'Unassigned',
                        'givenname': '',
                        'surname': '',
                        'searcharray': emailArray
                    }, 
                    ...activeDocuments
                ],
                'searchResults': [
                    {
                        'emailaddress': 'Unassigned',
                        'givenname': '',
                        'surname': '',
                        'searcharray': emailArray
                    }, 
                    ...activeDocuments
                ]
            });
  
        }
        
        function onError(error) {
    
          console.log(error);

            // Set error on toast
            setToast({
                'type': 'error',
                'message': 'Unable to load users',
            });
        
        }
        
        const unsubscribe = QueryListener('users', [query], onLoadChange, onLoadChange, onError);
        
        return () =>{
          unsubscribe();
        };
          
      // eslint-disable-next-line
      }, [query]);
      
    // Close menu on outside click
    useEffect(() => {

        // Function to handle outside clicks
        const handleClickOutside = (event) => {

            // Check if click is outside both dropdown and input field
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&inputRef.current && !inputRef.current.contains(event.target)) {
                closeDropdown();
            }

        };
        
        // Add event listener with capture phase to ensure it fires before other handlers
        document.addEventListener('mousedown', handleClickOutside, true);
        
        // Cleanup function
        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
        
    }, [searchData.hideDropdown]); // Add hideDropdown as dependency to re-attach when state changes
    

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

    return (
        <div className='flex flex-col w-full h-full relative px-[8px]'>

            {/* ------------------------------------------------------ */}
            {/*  Users Search Bar                                      */}
            {/* ------------------------------------------------------ */}

            {/* Search Bar */}
            <div 
                className={`grid grid-cols-[1fr_30px] justify-between items-center pl-2 py-1 rounded-sm border-1 border-solid border-[#dee2e6] min-w-[250px] ${disabled ? 'text-[#424242] bg-[#F8F8F8] cursor-text' : 'bg-white cursor-pointer'}`}
                ref={inputRef}
                onClick={() => {
                    if (!disabled) setSearchData({ 'hideDropdown': searchData?.newUserInput?.length === 0 ? false : !searchData?.hideDropdown })
                }}
                style={styleInput}            
            >

                {/* Search Input Field */}
                <input
                    id='email'
                    className='cursor-pointer p-1 rounded-md whitespace-nowrap overflow-ellipsis disabled:cursor-text'
                    style={styleInput}
                    type='text'
                    placeholder='Search user by email address'
                    value={searchData?.newUserInput}
                    onChange={(evt) => handleSearch(evt.target.value)}
                    onPaste={(evt) => handleSearch(evt.clipboardData.getData('email'))}
                    autoComplete='off'
                    disabled={disabled}
                >
                </input>
                
                <img src={EditIcon} alt='Edit' hidden={disabled}></img>

            </div>

            {/* Search Results */}
            <div 
                className={
                    (lastItem === true && lastItem !== undefined) ? 
                        'absolute z-10 bottom-12 w-full max-h-[250px] overflow-y-auto overflow-x-hidden bg-white shadow-md rounded-tl-none rounded-br-[5px] rounded-tr-none rounded-bl-[5px] border-[1px] border-[solid] border-[#D2D2D2]'
                    :
                        'absolute z-10 top-12 w-full max-h-[250px] overflow-y-auto overflow-x-hidden bg-white shadow-md rounded-tl-none rounded-br-[5px] rounded-tr-none rounded-bl-[5px] border-[1px] border-[solid] border-[#D2D2D2]'
                }
                ref={dropdownRef}
                style={styleInput}
                hidden={searchData?.hideDropdown}
            >

                {/* Results */}
                {
                    searchData?.searchResults?.length === 0 ?
                        <div className='font-medium text-[14px] px-3 py-[12px]'>
                            No results found.
                        </div>
                    :
                    searchData?.searchResults.map((user, index) => (
                        <div 
                            key={index} 
                            className='flex flex-row justify-between px-3 py-[12px] cursor-pointer rounded-sm hover:bg-[#F0F0F0]' 
                            onClick={() => handleSave(user)}
                        >
            
                            {/* User Detail */}
                            <div className='flex flex-col'>
                                <p className='m-0 p-0 font-medium text-[14px]'>{user?.givenname} {user?.surname}</p>
                                <p className='m-0 p-0 text-[14px]'>{user?.emailaddress}</p>
                            </div>
                        
                        </div>
                    ))
                }
                
                {/* Total */}
                <div className='text-sm font-medium px-4 py-2 border-t border-t-[#D8D8D8]' hidden={searchData.queryMessage?.length === 0}>
                    {searchData.queryMessage}
                </div>

            </div>
            
        </div>
    )

  //------------------------------------------------------
}
